import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";

import { transformAncet } from "@/features/chats";
import { useTrainerChats } from "../model/TrainerChatsContext";
import { useTrainerChatOutlet } from "./TrainerChatUserLayout";

import styles from "./TrainerChatSummaryPage.module.scss";

export const TrainerChatSummaryPage = () => {
  const navigate = useNavigate();
  const { userId, profile } = useTrainerChatOutlet();
  const { getCachedMessages } = useTrainerChats();

  const messagesCount = getCachedMessages(userId)?.length ?? "—";

  const ancetView = useMemo(
    () => transformAncet(profile.ancet ?? []),
    [profile.ancet],
  );
  const ancetPreview = useMemo(() => ancetView.slice(0, 6), [ancetView]);

  const trainingsSummary = useMemo(() => {
    if (!profile.trainings?.length) {
      return "Тренировок пока нет";
    }

    const upcoming = profile.trainings.filter((training) => {
      return training.status === 'wait' || training.status === null || training.status === undefined;
    }).length;

    return `${profile.trainings.length} всего · ${upcoming} предстоящих`;
  }, [profile.trainings]);

  const equipmentsSummary = useMemo(() => {
    if (!profile.equipments?.length) {
      return "Оборудование не указано";
    }

    const equipmentCount = new Set(
      profile.equipments.map((eq) => eq.equipment_id),
    ).size;

    return `${equipmentCount} ${equipmentCount === 1 ? "тип" : "типов"} оборудования`;
  }, [profile.equipments]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.profileCard}>
        <div className={styles.avatar}>
          {profile.fullname ? profile.fullname.charAt(0).toUpperCase() : "?"}
        </div>
        <div className={styles.profileInfo}>
          <span className={styles.profileName}>{profile.fullname}</span>
          {profile.username && (
            <span className={styles.profileUsername}>@{profile.username}</span>
          )}
        </div>
      </div>

      <button
        type="button"
        className={styles.backButton}
        onClick={() => navigate("/trainer/clients")}
      >
        ← Назад к клиентам
      </button>

      <button
        type="button"
        className={styles.requestsCard}
        onClick={() => navigate("chat")}
      >
        <span className={styles.requestsTitle}>Чат с клиентом</span>
        <span className={styles.requestsBadge}>{messagesCount}</span>
        <span className={styles.requestsArrow}>→</span>
      </button>

      <button
        type="button"
        className={styles.requestsCard}
        onClick={() => navigate("trainings")}
      >
        <span className={styles.requestsTitle}>Тренировки</span>
        <span className={styles.requestsArrow}>→</span>
      </button>

      <div className={styles.sections}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3>Анкета клиента</h3>
            <Link to="ancet" className={styles.sectionLink}>
              Посмотреть полностью
            </Link>
          </div>

          <ul className={styles.sectionList}>
            {ancetPreview.map((item) => (
              <li key={item.questNum}>
                <span className={styles.sectionLabel}>{item.title}</span>
                <span className={styles.sectionValue}>{item.value}</span>
              </li>
            ))}
            {!ancetPreview.length && (
              <li className={styles.sectionEmpty}>Анкета пока пустая</li>
            )}
          </ul>
        </div>

        <button
          type="button"
          className={styles.trainingsCard}
          onClick={() => navigate("trainings-program")}
        >
          <div className={styles.trainingsHeader}>
            <h3>Программа тренировок</h3>
            <span className={styles.trainingsArrow}>→</span>
          </div>
          <span className={styles.trainingsSummary}>{trainingsSummary}</span>
          <span className={styles.trainingsSubtitle}>
            Все тренировки: завершённые и предстоящие
          </span>
        </button>

        <button
          type="button"
          className={styles.trainingsCard}
          onClick={() => navigate("equipments")}
        >
          <div className={styles.trainingsHeader}>
            <h3>Оборудование клиента</h3>
            <span className={styles.trainingsArrow}>→</span>
          </div>
          <span className={styles.trainingsSummary}>{equipmentsSummary}</span>
          <span className={styles.trainingsSubtitle}>
            Доступное оборудование в зале клиента
          </span>
        </button>
      </div>
    </div>
  );
};

